import {useRef, useState} from 'react';
import {Alert, Tag, Tooltip} from 'antd';
import {
  AppstoreOutlined,
  FolderOpenOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import DBModal from 'components/modal/modal';
import {CategoryForm} from './CategoryForm';
import {Column, EmptyState, Row} from './Column';
import {FeatureForm} from './FeatureForm';
import {IssueForm} from './IssueForm';
import {FlowConnectors} from './FlowConnectors';
import {LANGUAGES, CLOSED_FORM_MODAL} from './constants';
import {useFlowBuilder} from './useFlowBuilder';
import styles from './FlowBuilder.module.scss';

const humanize = value =>
  String(value || '')
    .toLowerCase()
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const Toolbar = ({language, setLanguage, onRefresh}) => (
  <div className={styles.toolbar}>
    <div className={styles.titleBlock}>
      <h2>Guided Flow Builder</h2>
      <p>
        Author the customer guided-flow: <strong>Categories</strong> →{' '}
        <strong>Features</strong> → <strong>Issues</strong>. Pick a row to drill
        in.
      </p>
    </div>
    <div className={styles.toolbarRight}>
      <div className={styles.langPill}>
        {LANGUAGES.map(opt => (
          <button
            key={opt.value}
            type="button"
            className={language === opt.value ? styles.langPillActive : ''}
            onClick={() => setLanguage(opt.value)}>
            {opt.label}
          </button>
        ))}
      </div>
      <Tooltip title="Refresh">
        <button type="button" className={styles.refreshBtn} onClick={onRefresh}>
          <ReloadOutlined />
        </button>
      </Tooltip>
    </div>
  </div>
);

const matches = (text, term) =>
  (text || '').toLowerCase().includes(term.trim().toLowerCase());

const NoMatch = ({icon, term}) => (
  <EmptyState
    icon={icon}
    title="No matches"
    hint={`Nothing matches “${term}”.`}
  />
);

const CategoriesColumn = ({fb}) => {
  const [search, setSearch] = useState('');
  const rows = fb.categories.filter(c => matches(c.name, search));
  return (
    <Column
      title="Categories"
      count={fb.categories.length}
      onAdd={fb.openCreateCategory}
      search={search}
      onSearchChange={setSearch}
      searchPlaceholder="Search categories…">
      {fb.categoriesLoading && !fb.categories.length ? (
        <EmptyState
          icon={FolderOpenOutlined}
          title="Loading…"
          hint="Fetching categories."
        />
      ) : !fb.categories.length ? (
        <EmptyState
          icon={FolderOpenOutlined}
          title={`No categories for ${fb.language}`}
          hint="Click Add to create your first category."
        />
      ) : !rows.length ? (
        <NoMatch icon={FolderOpenOutlined} term={search} />
      ) : (
        rows.map(cat => (
          <Row
            key={cat.id}
            record={cat}
            flowId={`cat-${cat.id}`}
            active={cat.id === fb.selectedCategoryId}
            title={cat.name}
            icon={cat.iconUrl}
            onClick={() => {
              fb.setSelectedCategoryId(cat.id);
              fb.setSelectedFeatureId(null);
            }}
            onEdit={() => fb.openEditCategory(cat)}
            onDelete={() => fb.confirmDeleteCategory(cat)}
          />
        ))
      )}
    </Column>
  );
};

const FeaturesColumn = ({fb}) => {
  const [search, setSearch] = useState('');
  const rows = fb.features.filter(f => matches(f.name, search));
  return (
    <Column
      title="Features"
      count={fb.features.length}
      addDisabled={!fb.selectedCategoryId}
      onAdd={fb.openCreateFeature}
      showSearch={!!fb.selectedCategoryId}
      search={search}
      onSearchChange={setSearch}
      searchPlaceholder="Search features…">
      {!fb.selectedCategoryId ? (
        <EmptyState
          icon={AppstoreOutlined}
          title="No category selected"
          hint="Pick a category on the left to see its features."
        />
      ) : fb.featuresError ? (
        <Alert type="error" showIcon message={fb.featuresError} />
      ) : fb.featuresLoading && !fb.features.length ? (
        <EmptyState
          icon={AppstoreOutlined}
          title="Loading…"
          hint="Fetching features."
        />
      ) : !fb.features.length ? (
        <EmptyState
          icon={AppstoreOutlined}
          title="No features yet"
          hint="Click Add to create the first feature under this category."
        />
      ) : !rows.length ? (
        <NoMatch icon={AppstoreOutlined} term={search} />
      ) : (
        rows.map(feat => (
          <Row
            key={feat.id}
            record={feat}
            flowId={`feat-${feat.id}`}
            active={feat.id === fb.selectedFeatureId}
            title={feat.name}
            icon={feat.iconUrl}
            onClick={() => fb.setSelectedFeatureId(feat.id)}
            onEdit={() => fb.openEditFeature(feat)}
            onDelete={() => fb.confirmDeleteFeature(feat)}
          />
        ))
      )}
    </Column>
  );
};

const IssueSubtitle = ({issue}) => (
  <>
    <Tag color="purple" style={{margin: 0}}>
      {humanize(issue.stepTemplate)}
    </Tag>
    {issue.fallthrough ? (
      <Tooltip title="Escalate-first — selecting this issue skips the AI and routes straight to a human agent.">
        <Tag color="orange" style={{margin: 0}}>
          Escalate first
        </Tag>
      </Tooltip>
    ) : null}
    {issue.skillId ? (
      <Tag color="cyan" style={{margin: 0}}>
        Skill linked
      </Tag>
    ) : null}
    <span>FAQs: {(issue.faqPostIds || []).length}</span>
  </>
);

const IssuesColumn = ({fb}) => {
  const [search, setSearch] = useState('');
  const rows = fb.issues.filter(i => matches(i.title, search));
  return (
    <Column
      title="Issues"
      count={fb.issues.length}
      addDisabled={!fb.selectedFeatureId}
      onAdd={fb.openCreateIssue}
      showSearch={!!fb.selectedFeatureId}
      search={search}
      onSearchChange={setSearch}
      searchPlaceholder="Search issues…">
      {!fb.selectedFeatureId ? (
        <EmptyState
          icon={QuestionCircleOutlined}
          title="No feature selected"
          hint="Pick a feature in the middle column to see its issues."
        />
      ) : fb.issuesError ? (
        <Alert type="error" showIcon message={fb.issuesError} />
      ) : fb.issuesLoading && !fb.issues.length ? (
        <EmptyState
          icon={QuestionCircleOutlined}
          title="Loading…"
          hint="Fetching issues."
        />
      ) : !fb.issues.length ? (
        <EmptyState
          icon={QuestionCircleOutlined}
          title="No issues yet"
          hint="Click Add to create the first issue under this feature."
        />
      ) : !rows.length ? (
        <NoMatch icon={QuestionCircleOutlined} term={search} />
      ) : (
        rows.map(issue => (
          <Row
            key={issue.id}
            record={issue}
            flowId={`issue-${issue.id}`}
            title={issue.title}
            subtitle={<IssueSubtitle issue={issue} />}
            onClick={() => fb.openEditIssue(issue)}
            onEdit={() => fb.openEditIssue(issue)}
            onDelete={() => fb.confirmDeleteIssue(issue)}
          />
        ))
      )}
    </Column>
  );
};

export const FlowBuilder = () => {
  const fb = useFlowBuilder();
  const columnsRef = useRef(null);

  const selectedCategory = fb.categories.find(
    c => c.id === fb.selectedCategoryId,
  );
  const selectedFeature = fb.features.find(f => f.id === fb.selectedFeatureId);
  // When opening "edit", the record might belong to a different parent if the
  // selection has moved — fall back to the record's own ids if needed.
  const featureParent =
    fb.featureModal.mode === 'edit'
      ? fb.categories.find(c => c.id === fb.featureModal.record?.categoryId) ||
        selectedCategory
      : selectedCategory;
  const issueFeature =
    fb.issueModal.mode === 'edit'
      ? fb.features.find(f => f.id === fb.issueModal.record?.featureId) ||
        selectedFeature
      : selectedFeature;
  const issueCategory = issueFeature
    ? fb.categories.find(c => c.id === issueFeature.categoryId) ||
      selectedCategory
    : selectedCategory;

  return (
    <div className={styles.root}>
      <Toolbar
        language={fb.language}
        setLanguage={fb.setLanguage}
        onRefresh={fb.refreshAll}
      />

      {fb.categoriesError ? (
        <Alert
          type="error"
          showIcon
          message={fb.categoriesError}
          className={styles.errorBanner}
        />
      ) : null}

      <div className={styles.columns} ref={columnsRef}>
        <CategoriesColumn fb={fb} />
        <FeaturesColumn fb={fb} />
        <IssuesColumn fb={fb} />
        <FlowConnectors
          hostRef={columnsRef}
          selectedCategoryId={fb.selectedCategoryId}
          selectedFeatureId={fb.selectedFeatureId}
          features={fb.features}
          issues={fb.issues}
        />
      </div>

      <DBModal
        isOpen={fb.categoryModal.open}
        handleClose={() => fb.setCategoryModal(CLOSED_FORM_MODAL)}
        destroyOnClose
        content={
          <CategoryForm
            form={fb.categoryForm}
            mode={fb.categoryModal.mode}
            language={fb.language}
            onFinish={fb.submitCategory}
            submitting={fb.creatingCat || fb.updatingCat}
          />
        }
      />
      <DBModal
        isOpen={fb.featureModal.open}
        handleClose={() => fb.setFeatureModal(CLOSED_FORM_MODAL)}
        destroyOnClose
        content={
          <FeatureForm
            form={fb.featureForm}
            mode={fb.featureModal.mode}
            language={fb.language}
            parentCategory={featureParent}
            onFinish={fb.submitFeature}
            submitting={fb.creatingFeat || fb.updatingFeat}
          />
        }
      />
      <DBModal
        isOpen={fb.issueModal.open}
        handleClose={() => fb.setIssueModal(CLOSED_FORM_MODAL)}
        destroyOnClose
        width={800}
        content={
          <IssueForm
            form={fb.issueForm}
            mode={fb.issueModal.mode}
            language={fb.language}
            skillOptions={fb.skillOptions}
            skillsLoading={fb.skillsLoading}
            resolverCatalog={fb.resolverCatalog}
            resolverCatalogLoading={fb.resolverCatalogLoading}
            initialFaqs={fb.issueModal.record?.faqs}
            parentCategory={issueCategory}
            parentFeature={issueFeature}
            onFinish={fb.submitIssue}
            submitting={fb.creatingIssue || fb.updatingIssue}
          />
        }
      />
    </div>
  );
};
